import React, { useState, useEffect } from 'react';
import { X, Calendar, DollarSign, Building2, FileText, Clock, Shield, CheckCircle, Info, Package, Award, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { Tender } from '../types';
import ApplicationForm from './ApplicationForm';

interface TenderDetailProps {
  tender: Tender;
  onClose: () => void;
  onNavigate?: (direction: 'prev' | 'next') => void;
  showPrev?: boolean;
  showNext?: boolean;
  onCreateApplication?: (tender: Tender) => void;
}

const TenderDetail: React.FC<TenderDetailProps> = ({ tender, onClose, onNavigate, showPrev, showNext, onCreateApplication }) => {
  const { 
    organizations, 
    tenderTypes, 
    tenderStatuses, 
    businessLines,
    applications,
    tenders,
    updateTender
  } = useData();
  
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [currentTender, setCurrentTender] = useState<Tender>(tender);

  // Sincronizar el estado local con la prop tender cuando cambie
  useEffect(() => {
    setCurrentTender(tender);
  }, [tender]);

  const formatCurrency = (amount: number, currency: string = 'CLP') => {
    if (!amount) return 'No especificado';
    return `${currency} ${amount.toLocaleString('es-CL')}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getOrganizationName = (id: string) => {
    return organizations.find(org => org.id === id)?.name || 'No especificada';
  };

  const getTenderTypeName = (id: string) => {
    return tenderTypes.find(type => type.id === id)?.name || 'No especificado';
  };

  const getTenderStatusName = (id: string) => {
    return tenderStatuses.find(status => status.id === id)?.name || 'No especificado';
  };

  const getTenderStatusColor = (id: string) => {
    return tenderStatuses.find(status => status.id === id)?.color || '#6b7280';
  };

  const getBusinessLineName = (id: string) => {
    return businessLines.find(line => line.id === id)?.name || 'No especificada';
  };
  
  const getFlujoColor = (flujo: string = 'Disponible') => {
    switch (flujo) {
      case 'Disponible':
        return 'bg-blue-100 text-blue-800';
      case 'En Revision':
        return 'bg-purple-100 text-purple-800';
      case 'Aceptada':
        return 'bg-green-100 text-green-800';
      case 'Descartada':
        return 'bg-red-100 text-red-800';
      case 'Adjudicada':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Verificar si una licitación ya tiene una postulación
  const hasApplication = (tenderId: string) => {
    return applications.some(app => app.tender_id === tenderId);
  };

  // Verificar si una licitación está en estado "Publicada"
  const isPublishedTender = () => {
    const status = tenderStatuses.find(s => s.id === currentTender.status_id);
    return status?.name === 'Publicada';
  };

  // Verificar si se puede crear una postulación para esta licitación
  const canCreateApplication = () => {
    return isPublishedTender() && !hasApplication(currentTender.id);
  };
  
  const handleCreateApplication = () => {
    if (!canCreateApplication()) {
      if (!isPublishedTender()) {
        alert('Solo se pueden crear postulaciones para licitaciones en estado "Publicada".');
      } else if (hasApplication(currentTender.id)) {
        alert('Ya existe una postulación para esta licitación.');
      }
      return;
    }
    
    // Si hay una función onCreateApplication proporcionada, usarla
    if (onCreateApplication) {
      onCreateApplication(currentTender);
      return;
    }
    
    // Actualizar el flujo a "Aceptada"
    updateTender(currentTender.id, { flujo: 'Aceptada' });
    setCurrentTender({...currentTender, flujo: 'Aceptada'});
    
    setShowApplicationForm(true);
  };
  
  const handleCloseApplicationForm = () => {
    setShowApplicationForm(false);
  };
  
  const handleDescartar = async () => {
    if (hasApplication(currentTender.id)) {
      alert('No se puede descartar una licitación que ya tiene una postulación.');
      return;
    }
    
    if (!confirm('¿Estás seguro de que deseas marcar esta licitación como "Descartada"?')) {
      return;
    }
    
    console.log('Actualizando flujo a "Descartada" para tender:', currentTender.id);
    const updatedTender = await updateTender(currentTender.id, { flujo: 'Descartada' });
    
    if (updatedTender) {
      console.log('Tender actualizado exitosamente:', updatedTender);
      setCurrentTender(updatedTender);
    } else {
      console.error('Error al actualizar el tender');
      alert('Error al actualizar el flujo de la licitación');
    }
  };

  const handleRevisar = async () => {
    if (hasApplication(currentTender.id)) {
      alert('No se puede cambiar el flujo de una licitación que ya tiene una postulación.');
      return;
    }
    
    if (!confirm('¿Estás seguro de que deseas marcar esta licitación como "En Revision"?')) {
      return;
    }
    
    try {
      console.log('Actualizando flujo a "En Revision" para tender:', currentTender.id);
      const updatedTender = await updateTender(currentTender.id, { flujo: 'En Revision' });
      
      if (updatedTender) {
        console.log('Tender actualizado exitosamente:', updatedTender);
        setCurrentTender(updatedTender);
      } else {
        console.error('Error al actualizar el tender - respuesta null');
        alert('Error al actualizar el flujo de la licitación. Por favor, inténtalo de nuevo.');
      }
    } catch (error) {
      console.error('Error en handleRevisar:', error);
      alert('Error inesperado al actualizar el flujo de la licitación. Por favor, inténtalo de nuevo.');
    }
  };
  
  // Navegación entre licitaciones
  const sortedTenders = [...tenders].sort((a, b) => 
    new Date(a.closing_date).getTime() - new Date(b.closing_date).getTime()
  );
  
  const currentIndex = sortedTenders.findIndex(t => t.id === currentTender.id);
  const hasPrevious = showPrev !== undefined ? showPrev : currentIndex > 0;
  const hasNext = showNext !== undefined ? showNext : currentIndex < sortedTenders.length - 1;
  
  const goToPrevious = () => {
    if (onNavigate) {
      onNavigate('prev');
      return;
    }
    
    if (hasPrevious) {
      const prevTender = sortedTenders[currentIndex - 1];
      setCurrentTender(prevTender);
      
      // Actualizar el flujo a "En Revision" si está en "Disponible"
      if (prevTender.flujo === 'Disponible' || !prevTender.flujo) {
        updateTender(prevTender.id, { flujo: 'En Revision' });
        setCurrentTender({...prevTender, flujo: 'En Revision'});
      }
    }
  };
  
  const goToNext = () => {
    if (onNavigate) {
      onNavigate('next');
      return;
    }
    
    if (hasNext) {
      const nextTender = sortedTenders[currentIndex + 1];
      setCurrentTender(nextTender);
      
      // Actualizar el flujo a "En Revision" si está en "Disponible"
      if (nextTender.flujo === 'Disponible' || !nextTender.flujo) {
        updateTender(nextTender.id, { flujo: 'En Revision' });
        setCurrentTender({...nextTender, flujo: 'En Revision'});
      }
    }
  };
  

  
  if (showApplicationForm) {
    return (
      <ApplicationForm
        application={null}
        onClose={handleCloseApplicationForm}
        preselectedTenderId={currentTender.id}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Detalle de Licitación</h1>
          <p className="text-gray-600">Vista de solo lectura - Esta licitación no se puede modificar</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={goToPrevious}
            disabled={!hasPrevious}
            className={`p-2 rounded-lg transition-colors ${
              hasPrevious 
                ? 'text-gray-600 hover:text-blue-600 hover:bg-blue-50' 
                : 'text-gray-300 cursor-not-allowed'
            }`}
            title={hasPrevious ? "Ver licitación anterior" : "No hay licitación anterior"}
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
          
          <button
            onClick={goToNext}
            disabled={!hasNext}
            className={`p-2 rounded-lg transition-colors ${
              hasNext 
                ? 'text-gray-600 hover:text-blue-600 hover:bg-blue-50' 
                : 'text-gray-300 cursor-not-allowed'
            }`}
            title={hasNext ? "Ver licitación siguiente" : "No hay licitación siguiente"}
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
        {/* Header con código y estado */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-mono text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
              {currentTender.tender_code}
            </span>
            <span 
              className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
              style={{ 
                backgroundColor: `${getTenderStatusColor(currentTender.status_id)}20`,
                color: getTenderStatusColor(currentTender.status_id)
              }}
            >
              {getTenderStatusName(currentTender.status_id)}
            </span>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getFlujoColor(currentTender.flujo)}`}>
              {currentTender.flujo || 'Disponible'}
            </span>
            {currentTender.tender_line_id && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                {getBusinessLineName(currentTender.tender_line_id)}
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            {canCreateApplication() && (
              <button
                onClick={handleCreateApplication}
                className="bg-blue-600 text-white px-3 py-1 rounded-lg text-sm hover:bg-blue-700 transition-colors flex items-center"
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Nueva Postulación
              </button>
            )}
          </div>
        </div>

        {/* Información básica */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="lg:col-span-2">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{currentTender.title}</h2>
            <p className="text-gray-600">{currentTender.description}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Organización</label>
            <div className="flex items-center text-gray-900">
              <Building2 className="h-4 w-4 mr-2 text-blue-600" />
              {getOrganizationName(currentTender.organization_id)}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Licitación</label>
            <div className="flex items-center text-gray-900">
              <FileText className="h-4 w-4 mr-2 text-purple-600" />
              {getTenderTypeName(currentTender.type_id)}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Presupuesto</label>
            <div className="flex items-center text-gray-900">
              <DollarSign className="h-4 w-4 mr-2 text-green-600" />
              <span className="font-semibold text-green-600">
                {formatCurrency(currentTender.amount, currentTender.currency)}
              </span>
              {currentTender.contract_months && (
                <span className="ml-2 text-sm text-gray-500">
                  ({currentTender.contract_months} meses)
                </span>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de Publicación</label>
            <div className="flex items-center text-gray-900">
              <Calendar className="h-4 w-4 mr-2 text-blue-600" />
              {formatDate(currentTender.publish_date)}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de Cierre</label>
            <div className="flex items-center text-gray-900">
              <Clock className="h-4 w-4 mr-2 text-red-600" />
              {formatDateTime(currentTender.closing_date)}
            </div>
          </div>

          {currentTender.fecha_preguntas && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Límite para Preguntas</label>
              <div className="flex items-center text-gray-900">
                <Clock className="h-4 w-4 mr-2 text-purple-600" />
                {formatDateTime(currentTender.fecha_preguntas)}
              </div>
            </div>
          )}

          {currentTender.award_date && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de Adjudicación</label>
              <div className="flex items-center text-gray-900">
                <Calendar className="h-4 w-4 mr-2 text-green-600" />
                {formatDate(currentTender.award_date)}
              </div>
            </div>
          )}
        </div>

        {/* Items de la licitación - Grilla simplificada de solo lectura */}
        {currentTender.items && currentTender.items.length > 0 && (
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Package className="h-5 w-5 text-orange-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Items de la Licitación</h3>
              </div>
              <div className="flex items-center space-x-4 text-sm">
                <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full font-medium">
                  {currentTender.items_count || 0} unidades totales
                </span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-medium">
                  {currentTender.items.length} producto{currentTender.items.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Producto
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Unidad de Medida
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cantidad
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Proveedor
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Monto Unitario
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentTender.items.map((item, index) => (
                      <tr key={item.id || index} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {item.NombreProducto}
                          </div>
                          {item.CodigoProducto && (
                            <div className="text-xs text-gray-500 font-mono">
                              Código: {item.CodigoProducto}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-gray-900">
                            {item.UnidadMedida || '-'}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {item.Cantidad.toLocaleString()}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          {item.RutProveedor && item.NombreProveedor ? (
                            <div className="space-y-1">
                              <div className="flex items-center text-xs">
                                <Award className="h-3 w-3 text-green-600 mr-1" />
                                <span className="font-medium text-green-700">Adjudicado</span>
                              </div>
                              <div className="text-xs text-gray-600">
                                <div className="font-medium">{item.NombreProveedor}</div>
                                <div className="font-mono">{item.RutProveedor}</div>
                              </div>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">Sin adjudicar</span>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          {item.MontoUnitario ? (
                            <div className="text-sm font-medium text-green-600">
                              {formatCurrency(item.MontoUnitario, currentTender.currency)}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Resumen de adjudicaciones */}
            {currentTender.items.some(item => item.RutProveedor && item.NombreProveedor) && (
              <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <Award className="h-5 w-5 text-green-600 mr-2" />
                  <h4 className="text-sm font-medium text-green-900">Resumen de Adjudicaciones</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-green-700 font-medium">Items adjudicados:</span>
                    <span className="ml-2 text-green-900">
                      {currentTender.items.filter(item => item.RutProveedor && item.NombreProveedor).length} de {currentTender.items.length}
                    </span>
                  </div>
                  <div>
                    <span className="text-green-700 font-medium">Proveedores únicos:</span>
                    <span className="ml-2 text-green-900">
                      {new Set(currentTender.items.filter(item => item.RutProveedor).map(item => item.RutProveedor)).size}
                    </span>
                  </div>
                  <div>
                    <span className="text-green-700 font-medium">Monto total adjudicado:</span>
                    <span className="ml-2 text-green-900 font-medium">
                      {formatCurrency(
                        currentTender.items
                          .filter(item => item.MontoUnitario && item.RutProveedor)
                          .reduce((sum, item) => sum + (item.MontoUnitario! * item.Cantidad), 0),
                        currentTender.currency
                      )}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Información adicional */}
        {(currentTender.has_technical_visit || currentTender.has_gso || currentTender.has_gfc) && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Información Adicional</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {currentTender.has_technical_visit && (
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-2 text-orange-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Visita Técnica</p>
                    {currentTender.technical_visit_date && (
                      <p className="text-xs text-gray-600">
                        {formatDateTime(currentTender.technical_visit_date)}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {currentTender.has_gso && (
                <div className="flex items-center">
                  <Shield className="h-4 w-4 mr-2 text-purple-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">GSO</p>
                    <p className="text-xs text-gray-600">
                      {formatCurrency(currentTender.gso_amount || 0, currentTender.currency)}
                    </p>
                    {(currentTender as any).gso_delivery_date && (
                      <p className="text-xs text-gray-500">
                        Entrega: {formatDate((currentTender as any).gso_delivery_date)}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {currentTender.has_gfc && (
                <div className="flex items-center">
                  <Shield className="h-4 w-4 mr-2 text-indigo-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">GFC</p>
                    <p className="text-xs text-gray-600">
                      {formatCurrency(currentTender.gfc_amount || 0, currentTender.currency)}
                    </p>
                    {(currentTender as any).gfc_delivery_date && (
                      <p className="text-xs text-gray-500">
                        Entrega: {formatDate((currentTender as any).gfc_delivery_date)}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Requisitos */}
        {currentTender.requirements && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Requisitos</label>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-gray-900 whitespace-pre-wrap">{currentTender.requirements}</p>
            </div>
          </div>
        )}

        {/* Documentos */}
        {currentTender.documents && currentTender.documents.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Documentos</label>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="space-y-2">
                {currentTender.documents.map((doc, index) => (
                  <div key={index} className="flex items-center text-sm text-gray-900">
                    <FileText className="h-4 w-4 mr-2 text-blue-600" />
                    {doc}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Información de estado */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <Info className="h-5 w-5 text-blue-600 mr-3 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-blue-900 mb-1">Estado de la Licitación</h4>
              <p className="text-sm text-blue-700">
                Esta licitación está en estado "{getTenderStatusName(currentTender.status_id)}" y flujo "{currentTender.flujo || 'Disponible'}".
                {canCreateApplication() ? (
                  <span className="ml-1 font-medium">Puedes crear una postulación para esta licitación.</span>
                ) : (
                  hasApplication(currentTender.id) ? (
                    <span className="ml-1 font-medium">Ya existe una postulación para esta licitación.</span>
                  ) : (
                    <span className="ml-1 font-medium">No se pueden crear postulaciones para licitaciones que no estén en estado "Publicada".</span>
                  )
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Botones de acción */}
        <div className="flex justify-between pt-6 border-t border-gray-200">
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cerrar
            </button>
          </div>
          
          <div className="flex space-x-3">
            {(currentTender.flujo === 'Disponible' || !currentTender.flujo) && (
              <button
                onClick={handleRevisar}
                disabled={hasApplication(currentTender.id)}
                className={`px-4 py-2 rounded-lg transition-colors flex items-center ${
                  hasApplication(currentTender.id)
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                }`}
              >
                <Eye className="h-4 w-4 mr-2" />
                Revisar
              </button>
            )}
            
            {(currentTender.flujo === 'Disponible' || currentTender.flujo === 'En Revision' || !currentTender.flujo) && (
              <button
                onClick={handleDescartar}
                disabled={hasApplication(currentTender.id)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  hasApplication(currentTender.id)
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                }`}
              >
                Descartar
              </button>
            )}
            
            {canCreateApplication() && (
              <button
                onClick={handleCreateApplication}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Nueva Postulación
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Navegación entre licitaciones */}
      <div className="flex justify-between">
        <button
          onClick={goToPrevious}
          disabled={!hasPrevious}
          className={`flex items-center px-4 py-2 rounded-lg ${
            hasPrevious 
              ? 'text-blue-600 hover:bg-blue-50' 
              : 'text-gray-300 cursor-not-allowed'
          }`}
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          Anterior
        </button>
        
        <button
          onClick={goToNext}
          disabled={!hasNext}
          className={`flex items-center px-4 py-2 rounded-lg ${
            hasNext 
              ? 'text-blue-600 hover:bg-blue-50' 
              : 'text-gray-300 cursor-not-allowed'
          }`}
        >
          Siguiente
          <ChevronRight className="h-5 w-5 ml-1" />
        </button>
      </div>
    </div>
  );
};

export default TenderDetail;